/**
 * BreadCrumbsMenu component to display the navigation path
 * 
 * TODO:
 * - Change the structure of the component to be cleaner with less nested ifs
 * - Make use of hook loading statuses(?)
 */

import { useLocation } from "react-router-dom"
import BreadcrumbsButton from "./breadcrumbsButton"
import useStorages from "hooks/useStorages"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import { memo, useMemo } from "react"
import useCustomers from "@/hooks/useCustomers"
import useSuppliers from "@/hooks/useSuppliers"
import useProducts from "@/hooks/useProducts"
import useTransactions from "@/hooks/useTransactions"
import useTargetStorage from "@/hooks/useTargetStorage"

interface StorageCrumbNames {
    categoryName?: string
    locationName?: string
    customerName?: string
    supplierName?: string
    productName?: string
    transactionLabel?: string
}

const appendStorageSection = (crumbs: string[][], basePath: string, segments: string[], names: StorageCrumbNames) => {
    const [section, entityId, action] = segments
    if (!section) return

    let fullPath = basePath
    const push = (label: string, path: string) => {
        crumbs.push([label, path])
    }

    if (section === 'products') {
        fullPath += '/products'
        push('products', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('create', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.productName || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            }
        }
    } else if (section === 'edit') {
        fullPath += '/edit'
        push('edit', fullPath)
    } else if (section === 'access') {
        fullPath += '/access'
        push('titleManageAccess', fullPath)
    } else if (section === 'statistics') {
        fullPath += '/statistics'
        push('titleStatistics', fullPath)
    } else if (section === 'history') {
        fullPath += '/history'
        push('titleHistory', fullPath)
    } else if (section === 'categories') {
        fullPath += '/categories'
        push('categories', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('create', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.categoryName || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            } else if (action === 'edit_custom_inputs') {
                fullPath += '/edit_custom_inputs'
                push('editInputs', fullPath)
            }
        }
    } else if (section === 'locations') {
        fullPath += '/locations'
        push('locations', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('create', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.locationName || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            }
        }
    } else if (section === 'transactions') {
        fullPath += '/transactions'
        push('titleTransactions', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('titleCreate', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.transactionLabel || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            }
        }
    } else if (section === 'customers') {
        fullPath += '/customers'
        push('titleCustomers', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('titleCreateCustomer', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.customerName || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            }
        }
    } else if (section === 'suppliers') {
        fullPath += '/suppliers'
        push('titleSuppliers', fullPath)
        if (entityId === 'create') {
            fullPath += '/create'
            push('titleCreateSupplier', fullPath)
        } else if (entityId) {
            fullPath += `/${entityId}`
            push(names.supplierName || '...', fullPath)
            if (action === 'edit') {
                fullPath += '/edit'
                push('edit', fullPath)
            }
        }
    }
}

interface BreadCrumbsMenuProps {
    storageId?: string
    categoryId?: string
    locationId?: string
    customerId?: string
    supplierId?: string
    productId?: string
    transactionId?: string
}

const BreadCrumbsMenu: React.FC<BreadCrumbsMenuProps> = ({ 
    storageId, 
    categoryId, 
    locationId,
    customerId, 
    supplierId,
    productId,
    transactionId
}) => {

    const location = useLocation()
    const { targetStorageName } = useTargetStorage()
    
    const {
        category
    } = useCategories({storageId, categoryId})

    const {
        location: storageLocation
    } = useLocations({storageId, locationId})
    
    const {
        storage
    } = useStorages({ storageId })

    const {
        customer
    } = useCustomers({ storageId, customerId })

    const {
        supplier
    } = useSuppliers({ storageId, supplierId })

    const {
        product
    } = useProducts({ storageId, productId })

    const {
        transaction
    } = useTransactions({ storageId, transactionId })

    const breadcrumbs = useMemo(() => {
        const path = location.pathname.split('/').filter(Boolean)
        let fullPath = '';
        let crumbs: string[][] = [];

        if (path[0] === 'storages') {
            fullPath = '/storages';
            crumbs.push(['storages', fullPath]);
            if (path[1] === 'create') {
                fullPath += '/create';
                crumbs.push(['create', fullPath]);
            }
        } else if (path[0] === 'storage') {
            fullPath = '/storage';
            crumbs.push([storage?.name || targetStorageName || '...', fullPath]);
            appendStorageSection(crumbs, fullPath, path.slice(1), {
                categoryName: category?.name,
                locationName: storageLocation?.name,
                customerName: customer?.name,
                supplierName: supplier?.name,
                productName: product?.name,
                transactionLabel: transaction?.transactionId as string | undefined,
            });
        } else if (path[0] === 'account') {
            fullPath = '/account';
            crumbs = [['account', fullPath]];
            if (path[1] === 'general-settings') {
                fullPath += '/general-settings';
                crumbs.push(['titleGeneralSettings', fullPath]);
            } else if (path[1] === 'security-settings') {
                fullPath += '/security-settings';
                crumbs.push(['titleSecuritySettings', fullPath]);
            } else if (path[1] === 'avatar-settings') {
                fullPath += '/avatar-settings';
                crumbs.push(['titleAvatarSettings', fullPath]);
            } else if (path[1] === 'access-invitations') {
                fullPath += '/access-invitations';
                crumbs.push(['buttonAccessInvitations', fullPath]);
            }
        } else if (path[0] === 'notifications') {
            fullPath = '/notifications';
            crumbs = [['titleNotifications', fullPath]];
        } else if (path[0] === 'tutorials') {
            fullPath = '/tutorials';
            crumbs = [['titleTutorials', fullPath]];
            if (path[1] === 'search') {
                fullPath += '/search';
                crumbs.push(['titleSearchTutorial', fullPath]);
            }
        }
        return crumbs
    }, [location.pathname, storage, category, storageLocation, customer, product, transaction, supplier, targetStorageName])

    const displaySkeleton = useMemo(() => {
        return breadcrumbs?.some(breadcrumb => breadcrumb[0] === '...') || false
        //return false
    }, [breadcrumbs])

    return (
        <div className={`breadcrumbs-wrapper`}>
            <div className={`breadcrumbs-menu ${displaySkeleton ? `skeleton` : ``}`}>
                {breadcrumbs && breadcrumbs?.length > 3 && <BreadcrumbsButton text={displaySkeleton ? `` : `...`} />}
                {breadcrumbs?.slice(-3).map((breadcrumb, index) =>
                    <BreadcrumbsButton key={index} text={displaySkeleton ? `` : breadcrumb[0]} url={breadcrumb[1]} />)
                }
            </div>
        </div>
    )
}

export default memo(BreadCrumbsMenu)

